<template>
  <BaseTable
    :items="rows"
    :key-field="idField"
    :row-key="rowKey"
    :row-class="rowClass"
    :empty-text="emptyText"
    :empty-colspan="emptyColspan"
    :wrapper-class="wrapperClass"
    :table-class="tableClass"
    @row-click="(item, index) => $emit('row-click', item, index)"
  >
    <template #head>
      <slot name="head" />
    </template>

    <template #row="{ item: row, index }">
      <slot
        name="row"
        :item="row"
        :index="index"
        :toggle-expand="toggleExpand"
        :indent-px="row.depth * indentSize"
        :is-search-active="isSearchActive"
      >
        <!-- default row rendering (used when consumer doesn't provide a `row` slot) -->
        <td>
          <div style="display:flex; align-items:center; gap:8px;">
            <button
              v-if="row.hasChildren"
              @click.stop="toggleExpand(row[props.idField])"
              class="expand-btn"
              :aria-label="row.isExpanded ? 'بستن' : 'باز کردن'"
            >
              <ArrowRightSvg
                class="expand-icon"
                :class="{ 'is-open': row.isExpanded }"
                aria-hidden="true"
              />
            </button>

            <div :style="{ marginLeft: row.depth * indentSize + 'px' }">{{ row.title }}</div>
          </div>
        </td>

        <!-- placeholder for actions column (consumer can override by providing `row` slot) -->
        <td class="col-actions"></td>
      </slot>
    </template>

    <template #empty>
      <slot name="empty">{{ emptyText }}</slot>
    </template>
  </BaseTable>
</template>

<script setup>
import { computed, shallowRef, watch, ref } from 'vue'
import BaseTable from './BaseTable.vue'
import ArrowRightSvg from '@/components/svgs/ArrowRightSvg.vue'
/* eslint-disable no-undef */
const props = defineProps({
  items: { type: Array, default: () => [] },

  idField: { type: String, default: 'id' },
  parentIdField: { type: String, default: 'parentId' },

  // Search
  search: { type: String, default: '' },
  searchKeys: { type: Array, default: () => [] },
  searchFn: { type: Function, default: null },
  /**
   * Search strategy:
   * - 'local': filter/match inside currently loaded items (default)
   * - 'server': backend already filtered items; table only expands and optionally highlights via matchField
   */
  searchStrategy: {
    type: String,
    default: 'local',
    validator: v => ['local', 'server'].includes(v),
  },
  matchField: { type: String, default: 'isMatch' },

  // Tree UI
  indentSize: { type: Number, default: 16 },
  defaultExpandedIds: { type: Array, default: () => [] },

  /**
   * Lazy loading:
   * When provided, HierarchyTable will call loadChildren(parentId) the first time a node is expanded.
   * The function should return an array of flat items (same shape as items).
   */
  loadChildren: { type: Function, default: null },
  hasChildrenField: { type: String, default: 'hasChildren' },

  // Pass-through BaseTable props
  rowKey: { type: Function, default: null },
  rowClass: { type: [String, Array, Object, Function], default: '' },

  emptyText: { type: String, default: 'موردی برای نمایش وجود ندارد.' },
  emptyColspan: { type: Number, default: 1 },

  wrapperClass: { type: [String, Array, Object], default: 'table-wrapper' },
  tableClass: { type: [String, Array, Object], default: 'table' },
})

defineEmits(['row-click'])

function normalizeParentId(parentId) {
  if (parentId === 0 || parentId === '0') return null
  return parentId ?? null
}

function getField(item, key) {
  if (!item) return ''
  return item[key]
}

function defaultSearchMatch(item, normalizedQuery) {
  if (!normalizedQuery) return true

  const keys = Array.isArray(props.searchKeys) ? props.searchKeys : []
  if (keys.length === 0) return true

  const haystack = keys
    .map(k => getField(item, k))
    .filter(v => v != null)
    .join(' ')
    .toString()
    .toLowerCase()

  return haystack.includes(normalizedQuery)
}

const byId = shallowRef(new Map())
const childrenByParentId = shallowRef(new Map())

// internal list so we can merge lazily-loaded children without forcing parent to mutate props.items
const itemsState = ref([])

// prevent refetching the same parent
const loadedChildrenOf = ref(new Set())

const expandedIds = ref(new Set((props.defaultExpandedIds || []).filter(Boolean)))

function rebuildIndex(list) {
  const nextById = new Map()
  const nextChildrenByParentId = new Map()

  const safeList = Array.isArray(list) ? list : []

  for (const raw of safeList) {
    if (!raw) continue
    const id = raw[props.idField]
    if (id == null) continue

    const parentId = normalizeParentId(raw[props.parentIdField])

    // keep original fields; normalize parentId in the exposed row object later
    nextById.set(id, { ...raw, [props.parentIdField]: parentId })
  }

  for (const [, node] of nextById) {
    const pid = normalizeParentId(node[props.parentIdField])
    if (!nextChildrenByParentId.has(pid)) nextChildrenByParentId.set(pid, [])
    nextChildrenByParentId.get(pid).push(node)
  }

  byId.value = nextById
  childrenByParentId.value = nextChildrenByParentId
}

watch(
  () => [props.items, Array.isArray(props.items) ? props.items.length : 0, props.idField, props.parentIdField],
  () => {
    itemsState.value = Array.isArray(props.items) ? [...props.items] : []
    loadedChildrenOf.value = new Set() // reset lazy cache when the dataset is replaced
    rebuildIndex(itemsState.value)
  },
  { immediate: true }
)

const normalizedSearch = computed(() => (props.search || '').toString().trim().toLowerCase())
const isSearchActive = computed(() => normalizedSearch.value.length > 0)

function hasChildren(nodeId) {
  const children = childrenByParentId.value.get(nodeId)
  return Array.isArray(children) && children.length > 0
}

function isExpandable(node) {
  const id = node?.[props.idField]
  if (id == null) return false

  if (hasChildren(id)) return true

  const hasChildrenFlag = props.hasChildrenField ? node?.[props.hasChildrenField] : undefined
  if (hasChildrenFlag === true) return true

  // If lazy load is enabled but backend doesn't provide hasChildren,
  // allow expanding in normal mode. In search mode we avoid showing fake expand buttons.
  return typeof props.loadChildren === 'function' && !isSearchActive.value
}

function mergeItems(nextItems) {
  if (!Array.isArray(nextItems) || nextItems.length === 0) return

  const existing = new Map()
  for (const it of itemsState.value) {
    const id = it?.[props.idField]
    if (id != null) existing.set(id, true)
  }

  let changed = false
  const merged = [...itemsState.value]
  for (const it of nextItems) {
    const id = it?.[props.idField]
    if (id == null) continue
    if (existing.has(id)) continue
    merged.push(it)
    existing.set(id, true)
    changed = true
  }

  if (changed) {
    itemsState.value = merged
    rebuildIndex(itemsState.value)
  }
}

async function toggleExpand(nodeId) {
  if (isSearchActive.value) return // search mode always expanded

  const node = byId.value.get(nodeId)
  if (!node) return

  // If we are about to expand and lazy load is enabled, fetch children once.
  const isCurrentlyExpanded = expandedIds.value.has(nodeId)
  const willExpand = !isCurrentlyExpanded

  if (
    willExpand &&
    typeof props.loadChildren === 'function' &&
    !loadedChildrenOf.value.has(nodeId)
  ) {
    loadedChildrenOf.value = new Set(loadedChildrenOf.value).add(nodeId)

    try {
      const children = await props.loadChildren(nodeId)
      mergeItems(children)
    } catch (e) {
      // allow expand toggle even if fetch fails
      // eslint-disable-next-line no-console
      console.error('[HierarchyTable] loadChildren failed:', e)
    }
  }

  const next = new Set(expandedIds.value)
  if (next.has(nodeId)) next.delete(nodeId)
  else next.add(nodeId)
  expandedIds.value = next
}

const rows = computed(() => {
  const result = []

  const pushRow = (node, depth, matchIds) => {
    const id = node[props.idField]
    const childHasChildren = isExpandable(node)
    const expanded = isSearchActive.value ? true : expandedIds.value.has(id)

    result.push({
      ...node,
      depth,
      hasChildren: childHasChildren,
      isExpanded: expanded,
      isMatch: matchIds ? matchIds.has(id) : false,
    })

    return { childHasChildren, expanded }
  }

  const walkExpanded = (parentId, depth) => {
    const children = childrenByParentId.value.get(parentId) || []
    for (const child of children) {
      const id = child[props.idField]
      const { childHasChildren, expanded } = pushRow(child, depth, null)
      if (childHasChildren && expanded) walkExpanded(id, depth + 1)
    }
  }

  const walkSearch = (parentId, depth, includedIds, matchIds) => {
    const children = childrenByParentId.value.get(parentId) || []
    for (const child of children) {
      const id = child[props.idField]
      if (!includedIds.has(id)) continue

      const { childHasChildren } = pushRow(child, depth, matchIds)
      if (childHasChildren) walkSearch(id, depth + 1, includedIds, matchIds)
    }
  }

  if (!isSearchActive.value) {
    walkExpanded(null, 0)
    return result
  }

  // Server-driven search: show exactly what parent passed in (already filtered), fully expanded.
  if (props.searchStrategy === 'server') {
    const walkAll = (parentId, depth) => {
      const children = childrenByParentId.value.get(parentId) || []
      for (const child of children) {
        const id = child[props.idField]
        const childHasChildren = hasChildren(id) || (props.hasChildrenField && child?.[props.hasChildrenField] === true)

        result.push({
          ...child,
          depth,
          hasChildren: childHasChildren,
          isExpanded: true,
          isMatch: props.matchField ? child?.[props.matchField] === true : false,
        })

        if (childHasChildren) walkAll(id, depth + 1)
      }
    }

    walkAll(null, 0)
    return result
  }

  const q = normalizedSearch.value
  const matcher = typeof props.searchFn === 'function' ? props.searchFn : defaultSearchMatch

  const matchIds = new Set()
  for (const item of (Array.isArray(itemsState.value) ? itemsState.value : [])) {
    const id = item?.[props.idField]
    if (id == null) continue
    if (matcher(item, q)) matchIds.add(id)
  }

  // include matches + their ancestors
  const includedIds = new Set(matchIds)
  for (const id of matchIds) {
    let current = byId.value.get(id)
    const seen = new Set()
    while (current && !seen.has(current[props.idField])) {
      const cid = current[props.idField]
      seen.add(cid)

      const pid = normalizeParentId(current[props.parentIdField])
      if (pid == null) break

      includedIds.add(pid)
      current = byId.value.get(pid)
    }
  }

  // ensure roots that lead to matches stay visible
  const memo = new Map()
  const hasIncludedDescendant = (nodeId) => {
    if (memo.has(nodeId)) return memo.get(nodeId)
    const children = childrenByParentId.value.get(nodeId) || []
    let ok = includedIds.has(nodeId)
    if (!ok) {
      for (const ch of children) {
        const cid = ch[props.idField]
        if (hasIncludedDescendant(cid)) {
          ok = true
          break
        }
      }
    }
    memo.set(nodeId, ok)
    return ok
  }

  const roots = childrenByParentId.value.get(null) || []
  for (const r of roots) {
    const rid = r[props.idField]
    if (hasIncludedDescendant(rid)) includedIds.add(rid)
  }

  walkSearch(null, 0, includedIds, matchIds)
  return result
})
</script>
<style scoped>
/* expand button + arrow used by default row fallback */
.expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  background: transparent;
  cursor: pointer;
  font-weight: 700;
}
.expand-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(20, 118, 243, 0.12);
}
.expand-icon {
  width: 12px;
  height: 12px;
  display: inline-block;
  transition: transform 0.15s ease;
  transform-origin: center;
}
.expand-icon.is-open {
  transform: rotate(90deg);
}
</style>
